<template>
  <Teleport to="#teleports">
    <transition name="fade">
      <div
        v-if="shouldShow"
        class="opacity-100 fixed top-0 bottom-0 right-0 left-0 bg-overlay-default z-overlay"
        tabindex="0"
        role="button"
        aria-label="Stäng popup-fönstret"
        @click="close"
        @keyup.enter.self.prevent="close"
        @keyup.space.self.prevent="close"
      ></div>
    </transition>
    <transition name="slide">
      <div
        v-if="shouldShow"
        role="dialog"
        :aria-label="dialogTitel"
        class="inset-0 flex items-center justify-center p-0 overflow-hidden md:inset-auto md:w-720 md:p-24 fixed md:top-1/2 md:-translate-x-1/2 left-0 md:left-1/2 md:-translate-y-1/2 z-modal"
        @click.stop
      >
        <div
          ref="focusTarget"
          class="w-full h-full md:h-auto md:max-h-[90vh] md:max-w-[100vw] md:w-720 bg-surface-background rounded-md flex flex-col overflow-hidden"
        >
          <div
            class="sticky top-0 flex justify-between align-middle rounded-t-md border-b border-border-tertiary pt-10 pb-5 px-16"
          >
            <h2 class="text-24">{{ dialogTitel }}</h2>
            <IconButton
              aria-label="Stäng popup-fönstret"
              icon-name="x"
              @click="close"
              @keydown.enter.self.prevent="close"
              @keydown.space.self.prevent="close"
              @keydown.esc="close()"
            />
          </div>
          <div class="relative overflow-y-auto">
            <div class="px-16 pt-24">
              <slot />
            </div>
          </div>
          <div
            class="fixed md:sticky bottom-0 left-0 right-0 w-full bg-surface-primary z-modal mt-16"
          >
            <div class="border-t border-solid border-border-secondary">
              <slot name="modal-footer" />
            </div>
          </div>
        </div>
      </div>
    </transition>
  </Teleport>
</template>

<script lang="ts" setup>
import { watch, ref } from 'vue';
import { onKeyStroke } from '@vueuse/core';

interface Props {
  dialogTitel: string;
}

const props = defineProps<Props>();

const { deactivateFocusTrap, focusTarget } = useModalFocusTrap();

const shouldShow = defineModel<boolean>();
const emit = defineEmits(['onClose']);

const { activatePageScrollLock, deactivatePageScrollLock } =
  usePageScrollLockState(`modal_${props.dialogTitel}`);

onUnmounted(() => {
  deactivateFocusTrap();
});

const close = () => {
  deactivateFocusTrap();
  shouldShow.value = false;
  emit('onClose');
};

watch(shouldShow, (isOpen) => {
  if (isOpen) {
    activatePageScrollLock();
  } else {
    deactivatePageScrollLock();
  }
});
onKeyStroke(
  'Escape',
  (e) => {
    close();
  },
  { target: focusTarget },
);
</script>

<style scoped lang="scss">
.slide-enter-active,
.slide-leave-active {
  transition: all 0.3s ease-in-out;
}

.slide-enter-from,
.slide-leave-to {
  top: 100%;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s ease-in-out;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* reduced-motion */
@media (prefers-reduced-motion: reduce) {
  .fade-enter-active,
  .fade-leave-active {
    transition: none;
  }

  .slide-enter-active,
  .slide-leave-active {
    transition: none;
  }
}
</style>
